import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '../../config';
import Spinner from '../Global/Spinner';
import './scss/GitHubCallback.scss';

const GitHubCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const handleGitHubCallback = async () => {
      try {
        const code = searchParams.get('code');
        const stateParam = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          throw new Error(`GitHub OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        if (!stateParam) {
          throw new Error('No state parameter received');
        }

        // Parse state data - handle both old and new format
        let stateData;
        try {
          // Try new simple format first: "state|base64Domain|provider|action"
          if (stateParam.includes('|')) {
            const parts = stateParam.split('|');
            if (parts.length === 4) {
              stateData = {
                state: parts[0],
                originalDomain: atob(parts[1]),
                provider: parts[2],
                action: parts[3]
              };
            } else {
              throw new Error('Invalid simple state format');
            }
          } else {
            // Fallback to old format - just a random state token
            stateData = {
              state: stateParam,
              originalDomain: window.location.origin,
              provider: 'github',
              action: localStorage.getItem('github_auth_action') || 'login'
            };
          }
        } catch (e) {
          throw new Error('Invalid state parameter format');
        }

        const { state, provider, action } = stateData;
        let { originalDomain } = stateData;

        if (!state || !originalDomain || !provider || !action) {
          throw new Error('Missing required state data');
        }

        // Validate originalDomain to prevent open redirect attacks
        try {
          const domainUrl = new URL(originalDomain);
          const isAllowed = domainUrl.hostname === 'zoobbe.com' ||
            domainUrl.hostname.endsWith('.zoobbe.com') ||
            domainUrl.hostname === 'localhost' ||
            domainUrl.hostname === '127.0.0.1';
          if (!isAllowed) {
            originalDomain = window.location.origin;
          }
        } catch {
          originalDomain = window.location.origin;
        }

        // Verify state matches stored data (for old format compatibility)
        const storedStateData = localStorage.getItem('oauth_state_data');
        if (storedStateData) {
          try {
            const storedData = JSON.parse(storedStateData);
            if (storedData.state !== state) {
              throw new Error('State mismatch - possible CSRF attack');
            }
            localStorage.removeItem('oauth_state_data');
          } catch (e) {
            // If parsing fails, continue anyway (might be old format)
          }
        }

        // Clean up old localStorage keys
        localStorage.removeItem('github_oauth_state');
        localStorage.removeItem('github_auth_action');

        const authResponse = await fetch(config.API_URI + '/v1/users/auth/github', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            code: code,
            action: action,
            originalDomain: originalDomain
          }),
        });

        if (!authResponse.ok) {
          const errorData = await authResponse.json();
          throw new Error(errorData.message || 'Authentication failed');
        }

        await authResponse.json();
        setStatus('success');

        // Get user data and redirect
        const userResponse = await fetch(config.API_URI + '/v1/users/me', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          const username = userData.user.username;

          setTimeout(() => {
            // Redirect back to the original domain if different
            if (originalDomain && originalDomain !== window.location.origin) {
              window.location.href = `${originalDomain}/u/${username}/boards`;
            } else {
              window.location.href = `/u/${username}/boards`;
            }
          }, 1000);
        } else {
          // Fallback redirect
          setTimeout(() => {
            if (originalDomain && originalDomain !== window.location.origin) {
              window.location.href = originalDomain;
            } else {
              window.location.href = '/';
            }
          }, 1000);
        }

      } catch (error) {
        console.error('GitHub OAuth error:', error);
        setStatus('error');

        // Try to redirect back to original domain login page on error
        setTimeout(() => {
          const storedStateData = localStorage.getItem('oauth_state_data');
          if (storedStateData) {
            try {
              const storedData = JSON.parse(storedStateData);
              window.location.href = `${storedData.originalDomain}/login?error=${encodeURIComponent(error.message)}`;
              return;
            } catch (e) {
              // Ignore parsing errors
            }
          }
          navigate('/login');
        }, 2000);
      }
    };

    handleGitHubCallback();
  }, [searchParams, navigate]);

  return (
    <div className="github-callback-container">
      {status === 'processing' && (
        <>
          <Spinner size={40} strokeWidth={3} />
          <p className="callback-message">Authenticating with GitHub...</p>
        </>
      )}
      {status === 'success' && (
        <>
          <div className="success-icon">✓</div>
          <p className="callback-message">Success! Redirecting...</p>
        </>
      )}
      {status === 'error' && (
        <>
          <div className="error-icon">✗</div>
          <p className="callback-message">Authentication failed</p>
          <p className="redirect-message">Redirecting to login...</p>
        </>
      )}
    </div>
  );
};

export default GitHubCallback;
